import type { DragEndEvent, UniqueIdentifier } from "@dnd-kit/core";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useEffect, useMemo, useState } from "react";
import { useAtom } from "jotai";
import { ArrowRight, Play, Plus } from "lucide-react";
import { Link } from "react-router-dom";

import { createAttributeComponent } from "@coltorapps/builder-react";
import { builderStoreAtom } from "@/store";
import { useBpmDatasets, useTestExternalApi } from "@/hooks/useMasterData";
import HighlightCodeTextarea from "@/components/ui/highlight-code-textarea";
import CodeEditButton, {
  DEFAULT_TRIGGER_TEXT,
} from "@/components/ui/button/code-edit-button";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import CodeToggle from "@/components/ui/code-toggle";
import { DeleteIcon, DragIndicatorIcon } from "@/components/icons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/ui/modal";
import { RadioGroup, type RadioOption } from "@/components/ui/radio-group";
import { SearchableSelect } from "@/components/ui/select/searchable-select";
import { Select, type SelectOption } from "@/components/ui/select";
import { SingleSelect } from "@/components/ui/select/single-select";
import { formatError, ValidationError } from "@/components/ui/validation-error";
import { cn } from "@/utils/cn";

import {
  createDefaultModalDatasourceValue,
  modalDatasourceAttribute,
  type ModalDatasourceExternalColumn,
  type ModalDatasourceFilter,
  type ModalDatasourceFilterMode,
  type ModalDatasourceMasterDataColumn,
  type ModalDatasourceValue,
} from "./definition";
import type {
  ColumnType,
  DatasetDefinition,
  DatasetField,
  ExternalApiRequestConfig,
} from "@/types/master-data-dataset";
import type { Operator } from "@/types/flow";
import type { FormSchema } from "@/types/domain";
import { ExpressionDefault, ExpressionType } from "@/const/expression";

type BuilderEntityLike = {
  attributes: {
    name: string;
    label: {
      isReference?: boolean;
      reference?: string;
      value?: string;
    };
  };
};

const typeOptions: RadioOption[] = [
  { value: "master-data", label: "Master Data" },
  { value: "external-api", label: "External API" },
];

const columnTypeOptions: SelectOption<ColumnType>[] = [
  { label: "text", value: "text", key: "text" },
  { label: "boolean", value: "boolean", key: "boolean" },
  { label: "number", value: "number", key: "number" },
  { label: "date", value: "date", key: "date" },
];

const operatorOptions: SelectOption<Operator>[] = [
  { label: "=", value: "==", key: "==" },
];

const DEFAULT_EXTERNAL_API_EXAMPLE = {
  url: "https://jsonplaceholder.typicode.com/users",
  method: "GET",
  headers: {
    Accept: "application/json",
  },
} satisfies ExternalApiRequestConfig;

const DEFAULT_API_CONFIG_COMMENTED = JSON.stringify(
  DEFAULT_EXTERNAL_API_EXAMPLE,
  null,
  2,
)
  .split("\n")
  .map((line) => `// ${line}`)
  .join("\n");

const emptyApiConfig: ExternalApiRequestConfig = {
  url: "",
  method: "GET",
};

const stripCommentLines = (value: string) =>
  value
    .split("\n")
    .filter((line) => !line.trim().startsWith("//"))
    .join("\n")
    .trim();

const formatApiConfigForEditor = (apiConfig: ExternalApiRequestConfig) =>
  JSON.stringify(apiConfig) === JSON.stringify(emptyApiConfig)
    ? DEFAULT_API_CONFIG_COMMENTED
    : JSON.stringify(apiConfig, null, 2);

function EnvironmentVariableHint() {
  return (
    <div className="inline-flex items-center gap-2">
      <div className="flex items-center gap-2">
        <span className="text-base leading-6 font-normal text-gray-500">
          Use `{"${VARIABLE_NAME}"}` to reference environment variables.
        </span>
      </div>
      <Link
        to="/master-data?tab=variables"
        className="text-base leading-6 font-medium text-blue-600 hover:underline"
      >
        View
      </Link>
    </div>
  );
}

const ApiConfigHelpText = () => (
  <p className="text-sm leading-[22px] text-primary-text">
    Define `api_config` with `url`, `method`, optional `headers`, and optional
    `body`.
  </p>
);

const parseApiConfig = (value: string): ExternalApiRequestConfig => {
  const parsed = JSON.parse(value) as ExternalApiRequestConfig;
  const method = parsed.method ?? "GET";

  return {
    url: parsed.url ?? "",
    method: ["GET", "POST", "PUT"].includes(method) ? method : "GET",
    headers: parsed.headers ?? undefined,
    body: parsed.body ?? undefined,
  };
};

const resolvePath = (source: unknown, rawPath: string): unknown => {
  const path = rawPath.trim();
  if (!path) return source;

  return path
    .split(".")
    .filter(Boolean)
    .reduce<unknown>((acc, segment) => {
      if (acc === null || acc === undefined) return undefined;
      if (typeof acc !== "object") return undefined;
      return (acc as Record<string, unknown>)[segment];
    }, source);
};

const getDatasetFields = (dataset?: DatasetDefinition) => dataset?.fields ?? [];

const createFilter = (): ModalDatasourceFilter => ({
  columnKey: "",
  operator: "==",
  value: "",
});

const createExternalApiMapping = (): ModalDatasourceExternalColumn => ({
  columnKey: "",
  displayName: "",
  type: "text",
  formField: "",
});

const createLocalId = (): UniqueIdentifier =>
  globalThis.crypto?.randomUUID?.() ??
  `sortable-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const syncLocalIds = (
  currentIds: UniqueIdentifier[],
  targetLength: number,
): UniqueIdentifier[] => {
  const nextIds = currentIds.slice(0, targetLength);

  while (nextIds.length < targetLength) {
    nextIds.push(createLocalId());
  }

  return nextIds;
};

const getDatasetFieldType = (
  fields: DatasetField[],
  columnKey?: string,
): ColumnType | undefined =>
  fields.find((field) => field.name === columnKey)?.type;

const getAvailableFilterColumnOptions = (
  options: SelectOption<string>[],
  filters: ModalDatasourceFilter[],
  currentIndex: number,
): SelectOption<string>[] => {
  const currentColumnKey = filters[currentIndex]?.columnKey?.trim();
  const usedByOtherFilters = new Set(
    filters
      .filter((_, index) => index !== currentIndex)
      .map((filter) => filter.columnKey.trim())
      .filter(Boolean),
  );

  return options.filter(
    (option) =>
      option.value === currentColumnKey ||
      !usedByOtherFilters.has(option.value),
  );
};

const getNextAvailableFilterColumnKey = (
  options: SelectOption<string>[],
  filters: ModalDatasourceFilter[],
): string =>
  options.find(
    (option) =>
      !filters.some((filter) => filter.columnKey.trim() === option.value),
  )?.value ?? "";

const normalizeCurrentValue = (value: unknown): ModalDatasourceValue => {
  if (!value || typeof value !== "object" || !("type" in value)) {
    return createDefaultModalDatasourceValue();
  }

  const candidate = value as ModalDatasourceValue;
  if (candidate.type === "external-api" || candidate.type === "master-data") {
    return candidate;
  }

  return createDefaultModalDatasourceValue();
};

type ExternalApiEditorProps = {
  value: Extract<ModalDatasourceValue, { type: "external-api" }>;
  formFieldOptions: SelectOption<string>[];
  formSchema?: FormSchema;
  onChange: (
    value: Extract<ModalDatasourceValue, { type: "external-api" }>,
  ) => void;
  onClose: () => void;
  onSave: () => void;
};

type ExternalApiEditorTab = "api-config" | "display-value-config";

const externalApiEditorTabs: Array<{
  label: string;
  value: ExternalApiEditorTab;
}> = [
  { label: "API Config", value: "api-config" },
  { label: "Display Value Config", value: "display-value-config" },
];

type ExternalApiMappingRowProps = {
  id: UniqueIdentifier;
  index: number;
  mapping: ModalDatasourceExternalColumn;
  formFieldOptions: SelectOption<string>[];
  responseColumnOptions: SelectOption<string>[];
  onUpdate: (
    index: number,
    updates: Partial<ModalDatasourceExternalColumn>,
  ) => void;
  onRemove: (index: number) => void;
};

function ExternalApiMappingRow({
  id,
  index,
  mapping,
  formFieldOptions,
  responseColumnOptions,
  onUpdate,
  onRemove,
}: ExternalApiMappingRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={cn(
        "grid grid-cols-[20px_minmax(0,1.1fr)_24px_minmax(0,1.1fr)_minmax(0,0.95fr)_24px_minmax(0,1.1fr)_46px] items-center gap-2.5",
        isDragging && "z-10 opacity-70",
      )}
    >
      <button
        type="button"
        className="flex h-12 cursor-grab touch-none items-center justify-center text-primary-text active:cursor-grabbing"
        aria-label="Reorder mapping"
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon className="h-5 w-5 text-primary-text" />
      </button>
      <Input
        value={mapping.columnKey}
        onChange={(event) =>
          onUpdate(index, {
            columnKey: event.target.value,
          })
        }
        placeholder={responseColumnOptions[index]?.value ?? "response_key"}
      />
      <div className="flex h-12 items-center justify-center text-primary-text">
        <ArrowRight className="h-4 w-4" />
      </div>
      <Input
        value={mapping.displayName}
        onChange={(event) =>
          onUpdate(index, {
            displayName: event.target.value,
          })
        }
        placeholder="Display field"
      />
      <SingleSelect
        value={mapping.type}
        options={columnTypeOptions}
        onChange={(nextType) =>
          onUpdate(index, {
            type: nextType,
          })
        }
      />
      <div className="flex h-12 items-center justify-center text-primary-text">
        <ArrowRight className="h-4 w-4" />
      </div>
      <SearchableSelect
        mode="single"
        options={formFieldOptions}
        value={mapping.formField}
        placeholder="Select"
        onChange={(formField) =>
          onUpdate(index, {
            formField: String(formField ?? ""),
          })
        }
      />
      <Button
        type="button"
        variant="icon"
        size="icon"
        className="h-11 w-[46px]"
        onClick={() => onRemove(index)}
        aria-label="Remove mapping"
      >
        <DeleteIcon className="h-6 w-6 text-primary-text" />
      </Button>
    </div>
  );
}

function ExternalApiEditor({
  value,
  formFieldOptions,
  formSchema,
  onChange,
  onClose,
  onSave,
}: ExternalApiEditorProps) {
  const testExternalApi = useTestExternalApi();
  const [activeTab, setActiveTab] =
    useState<ExternalApiEditorTab>("api-config");
  const [mappingIds, setMappingIds] = useState<UniqueIdentifier[]>(() =>
    value.config.field_mappings.mappings.map(() => createLocalId()),
  );
  const [apiConfigText, setApiConfigText] = useState(() =>
    formatApiConfigForEditor(value.config.api_config),
  );
  const [testResponse, setTestResponse] = useState("");
  const [apiConfigError, setApiConfigError] = useState("");
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    setApiConfigText(formatApiConfigForEditor(value.config.api_config));
  }, [value.config.api_config]);

  useEffect(() => {
    setMappingIds((currentIds) =>
      syncLocalIds(currentIds, value.config.field_mappings.mappings.length),
    );
  }, [value.config.field_mappings.mappings.length]);

  const updateConfig = (
    updates: Partial<
      Extract<ModalDatasourceValue, { type: "external-api" }>["config"]
    >,
  ) => {
    onChange({
      ...value,
      config: {
        ...value.config,
        ...updates,
      },
    });
  };
  const filterMode = value.config.filterMode ?? "manual";
  const filterExpression =
    value.config.filterExpression ??
    ExpressionDefault[ExpressionType.DropdownFilter];
  const handleFilterModeChange = (mode: ModalDatasourceFilterMode) => {
    updateConfig({
      filterMode: mode,
      filters: mode === "code" ? [] : value.config.filters,
      filterExpression: mode === "code" ? filterExpression : undefined,
    });
  };

  const handleApiConfigChange = (nextValue: string) => {
    setApiConfigText(nextValue);
    const meaningfulValue = stripCommentLines(nextValue);

    if (!meaningfulValue) {
      updateConfig({ api_config: emptyApiConfig });
      setApiConfigError("");
      return;
    }

    try {
      updateConfig({ api_config: parseApiConfig(meaningfulValue) });
      setApiConfigError("");
    } catch {
      setApiConfigError("API config must be valid JSON.");
    }
  };

  const handleRunTest = async () => {
    try {
      const response = await testExternalApi.mutateAsync(
        value.config.api_config,
      );
      setTestResponse(JSON.stringify(response.data ?? null, null, 2));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to test external API.";
      setTestResponse(JSON.stringify({ error: message }, null, 2));
    }
  };

  const parsedTestResponse = useMemo(() => {
    if (!testResponse.trim()) return undefined;
    try {
      return JSON.parse(testResponse) as unknown;
    } catch {
      return undefined;
    }
  }, [testResponse]);

  const recordsSource = useMemo(() => {
    if (!parsedTestResponse) return undefined;
    return resolvePath(
      parsedTestResponse,
      value.config.field_mappings.records_path,
    );
  }, [parsedTestResponse, value.config.field_mappings.records_path]);

  const responseColumnOptions = useMemo<SelectOption<string>[]>(() => {
    const source = Array.isArray(recordsSource)
      ? recordsSource[0]
      : recordsSource;
    if (!source || typeof source !== "object" || Array.isArray(source))
      return [];

    return Object.keys(source).map((key) => ({
      label: key,
      value: key,
      key,
    }));
  }, [recordsSource]);

  const filterColumnOptions = useMemo<SelectOption<string>[]>(() => {
    const keys = new Set(
      value.config.field_mappings.mappings
        .map((mapping) => mapping.columnKey.trim())
        .filter(Boolean),
    );

    responseColumnOptions.forEach((option) => keys.add(option.value));

    return Array.from(keys).map((key) => ({ label: key, value: key, key }));
  }, [responseColumnOptions, value.config.field_mappings.mappings]);
  const canAddFilter =
    getNextAvailableFilterColumnKey(
      filterColumnOptions,
      value.config.filters,
    ) !== "";

  const updateMapping = (
    index: number,
    updates: Partial<ModalDatasourceExternalColumn>,
  ) => {
    const mappings = value.config.field_mappings.mappings.map(
      (mapping, current) =>
        current === index ? { ...mapping, ...updates } : mapping,
    );

    updateConfig({
      field_mappings: {
        ...value.config.field_mappings,
        mappings,
      },
    });
  };

  const removeMapping = (index: number) => {
    setMappingIds((currentIds) =>
      currentIds.filter((_, current) => current !== index),
    );
    updateConfig({
      field_mappings: {
        ...value.config.field_mappings,
        mappings: value.config.field_mappings.mappings.filter(
          (_, current) => current !== index,
        ),
      },
    });
  };

  const addMapping = () => {
    setMappingIds((currentIds) => [...currentIds, createLocalId()]);
    updateConfig({
      field_mappings: {
        ...value.config.field_mappings,
        mappings: [
          ...value.config.field_mappings.mappings,
          createExternalApiMapping(),
        ],
      },
    });
  };

  const handleMappingDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const oldIndex = mappingIds.findIndex(
      (mappingId) => mappingId === active.id,
    );
    const newIndex = mappingIds.findIndex((mappingId) => mappingId === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    setMappingIds((currentIds) => arrayMove(currentIds, oldIndex, newIndex));
    updateConfig({
      field_mappings: {
        ...value.config.field_mappings,
        mappings: arrayMove(
          value.config.field_mappings.mappings,
          oldIndex,
          newIndex,
        ),
      },
    });
  };

  const updateFilter = (
    index: number,
    updates: Partial<ModalDatasourceFilter>,
  ) => {
    updateConfig({
      filters: value.config.filters.map((filter, current) =>
        current === index ? { ...filter, ...updates } : filter,
      ),
    });
  };

  const addFilter = () => {
    const nextColumnKey = getNextAvailableFilterColumnKey(
      filterColumnOptions,
      value.config.filters,
    );
    if (!nextColumnKey) return;

    updateConfig({
      filters: [
        ...value.config.filters,
        {
          ...createFilter(),
          columnKey: nextColumnKey,
        },
      ],
    });
  };

  const removeFilter = (index: number) => {
    updateConfig({
      filters: value.config.filters.filter((_, current) => current !== index),
    });
  };

  const saveDisabled = Boolean(apiConfigError);

  return (
    <Modal
      isOpen
      close={onClose}
      size="full"
      className="max-w-[1160px] max-h-[90dvh]"
      closeOnOverlayClick={false}
    >
      <div className="flex min-h-0 w-full flex-1 flex-col gap-[30px] overflow-hidden bg-white p-[30px]">
        <h2 className="text-center text-2xl font-semibold leading-[30px] text-dark">
          External API Config
        </h2>

        <div className="grid shrink-0 grid-cols-2 gap-2.5">
          {externalApiEditorTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setActiveTab(tab.value)}
              className={cn(
                "h-11 rounded-[8px] border px-5 text-base font-medium transition-colors",
                activeTab === tab.value
                  ? "border-giant-blue bg-giant-blue text-white"
                  : "border-stroke bg-white text-dark",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pr-1">
          {activeTab === "api-config" ? (
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_1px_minmax(0,1fr)]">
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-2.5">
                  <div className="flex flex-col gap-1">
                    <Label className="pb-0 text-base font-medium text-dark">
                      External API
                    </Label>
                    <EnvironmentVariableHint />
                  </div>
                  <HighlightCodeTextarea
                    value={apiConfigText}
                    onChange={handleApiConfigChange}
                    minLines={12}
                    placeholder="Type here..."
                    language="json"
                    className="min-h-[288px]"
                  />
                  {apiConfigError ? (
                    <p className="text-sm leading-[22px] text-red">
                      {apiConfigError}
                    </p>
                  ) : (
                    <ApiConfigHelpText />
                  )}
                </div>
              </div>

              <div className="hidden bg-stroke lg:block" />

              <div className="flex flex-col gap-2.5">
                <div className="flex items-center justify-between gap-4">
                  <Label className="pb-0 text-base font-medium text-dark">
                    Test API Response
                  </Label>
                  <button
                    type="button"
                    onClick={handleRunTest}
                    className="inline-flex items-center gap-1.5 text-base font-medium text-giant-blue disabled:cursor-not-allowed disabled:text-giant-blue/60"
                    disabled={
                      Boolean(apiConfigError) || testExternalApi.isPending
                    }
                  >
                    <Play className="h-4 w-4 fill-current" />
                    {testExternalApi.isPending ? "Running..." : "Run test"}
                  </button>
                </div>

                <pre className="min-h-[350px] overflow-auto rounded-[8px] bg-dark p-5 font-mono text-sm leading-5 text-white">
                  {testResponse || " "}
                </pre>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-[5px]">
                <Label className="pb-0 text-base font-medium text-dark">
                  Records path
                </Label>
                <Input
                  value={value.config.field_mappings.records_path}
                  onChange={(event) =>
                    updateConfig({
                      field_mappings: {
                        ...value.config.field_mappings,
                        records_path: event.target.value,
                      },
                    })
                  }
                  placeholder="data.items"
                />
                <p className="text-sm leading-[22px] text-primary-text">
                  Dot-notation path to the records array. Leave empty if
                  response is already an array.
                </p>
              </div>

              <div className="flex min-h-0 flex-col gap-2.5">
                <Label className="pb-0 text-base font-medium text-dark">
                  Display Columns
                </Label>

                <div className="overflow-x-auto">
                  <div className="min-w-[900px]">
                    <div className="grid grid-cols-[20px_minmax(0,1.1fr)_24px_minmax(0,1.1fr)_minmax(0,0.95fr)_24px_minmax(0,1.1fr)_46px] items-center gap-2.5 pb-2.5 text-base font-medium text-dark">
                      <div />
                      <div>JSON path</div>
                      <div />
                      <div>Display field</div>
                      <div>Display Type</div>
                      <div />
                      <div>Set Mapping Value</div>
                      <div />
                    </div>

                    {value.config.field_mappings.mappings.length === 0 ? (
                      <div className="flex max-h-[288px] min-h-20 bg-gray items-center justify-center rounded-[8px] bg-gray-1 text-base font-normal text-secondary-text">
                        No data
                      </div>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleMappingDragEnd}
                      >
                        <SortableContext
                          items={mappingIds}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="flex flex-col gap-2.5">
                            {value.config.field_mappings.mappings.map(
                              (mapping, index) => (
                                <ExternalApiMappingRow
                                  key={mappingIds[index] ?? index}
                                  id={mappingIds[index] ?? index}
                                  index={index}
                                  mapping={mapping}
                                  formFieldOptions={formFieldOptions}
                                  responseColumnOptions={responseColumnOptions}
                                  onUpdate={updateMapping}
                                  onRemove={removeMapping}
                                />
                              ),
                            )}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                </div>

                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className={cn("w-fit px-0 text-base", "text-giant-blue")}
                  onClick={addMapping}
                  icon={<Plus className="h-4 w-4" />}
                >
                  Add display column
                </Button>
              </div>

              <div className="flex flex-col gap-[11px]">
                <div className="flex items-center justify-between gap-4">
                  <Label className="pb-0 text-base font-medium text-dark">
                    Filter Configuration
                  </Label>
                  <CodeToggle
                    value={filterMode}
                    onChange={handleFilterModeChange}
                  />
                </div>

                {filterMode === "code" ? (
                  <div className="flex flex-col gap-1.5">
                    <Label className="pb-0 text-base font-medium text-dark">
                      Expression
                    </Label>
                    <CodeEditButton
                      variant="reference"
                      formSchema={formSchema}
                      value={filterExpression}
                      trigger={filterExpression}
                      onSave={(nextExpression) => {
                        updateConfig({
                          filterMode: "code",
                          filterExpression: nextExpression,
                        });
                      }}
                    />
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-[minmax(0,1fr)_100px_minmax(0,1fr)_46px] items-center gap-2.5 text-base font-medium text-dark">
                      <div>Filter Column</div>
                      <div>Operator</div>
                      <div>Value</div>
                      <div />
                    </div>

                    <div className="flex flex-col gap-2.5">
                      {value.config.filters.map((filter, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-[minmax(0,1fr)_100px_minmax(0,1fr)_46px] items-center gap-2.5"
                        >
                          <Select
                            options={getAvailableFilterColumnOptions(
                              filterColumnOptions,
                              value.config.filters,
                              index,
                            )}
                            value={filter.columnKey}
                            placeholder="Select a column"
                            onChange={(columnKey: string) =>
                              updateFilter(index, { columnKey })
                            }
                          />
                          <Select
                            options={operatorOptions}
                            value={filter.operator}
                            placeholder="Operator"
                            onChange={(operator: Operator) =>
                              updateFilter(index, { operator })
                            }
                          />
                          <Input
                            value={String(filter.value ?? "")}
                            onChange={(event) =>
                              updateFilter(index, { value: event.target.value })
                            }
                            placeholder="Value"
                          />
                          <Button
                            type="button"
                            variant="icon"
                            size="icon"
                            className="h-11 w-[46px]"
                            onClick={() => removeFilter(index)}
                            aria-label="Remove filter"
                          >
                            <DeleteIcon className="h-6 w-6 text-primary-text" />
                          </Button>
                        </div>
                      ))}
                    </div>

                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className={cn(
                        "w-fit px-0 text-base",
                        "disabled:bg-transparent",
                        !canAddFilter
                          ? "text-secondary-text hover:no-underline"
                          : "text-giant-blue",
                      )}
                      onClick={addFilter}
                      icon={<Plus className="h-4 w-4" />}
                      disabled={!canAddFilter}
                    >
                      Add condition
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-center gap-[18px]">
          <Button
            type="button"
            variant="secondary"
            className="w-[190px]"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="w-[190px]"
            onClick={onSave}
            disabled={saveDisabled}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}

type MasterDataEditorProps = {
  value: Extract<ModalDatasourceValue, { type: "master-data" }>;
  formFieldOptions: SelectOption<string>[];
  formSchema?: FormSchema;
  onChange: (
    value: Extract<ModalDatasourceValue, { type: "master-data" }>,
  ) => void;
  onClose: () => void;
  onSave: () => void;
};

type MasterDataRow = {
  columnKey: string;
  displayName: string;
  formField?: string;
  selected: boolean;
  type: ColumnType;
};

type MasterDataColumnRowProps = {
  row: MasterDataRow;
  formFieldOptions: SelectOption<string>[];
  onToggle: (columnKey: string, checked: boolean) => void;
  onUpdate: (
    columnKey: string,
    updates: Partial<ModalDatasourceMasterDataColumn>,
  ) => void;
};

function MasterDataColumnRow({
  row,
  formFieldOptions,
  onToggle,
  onUpdate,
}: MasterDataColumnRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: row.columnKey,
    disabled: !row.selected,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={cn(
        "grid grid-cols-[20px_20px_minmax(0,1fr)_24px_minmax(0,1fr)_24px_minmax(0,1fr)] items-center gap-2.5",
        isDragging && "z-10 opacity-70",
      )}
    >
      <button
        type="button"
        className={cn(
          "flex h-11 items-center justify-center text-secondary-text",
          row.selected
            ? "cursor-grab touch-none active:cursor-grabbing"
            : "cursor-default opacity-40",
        )}
        aria-label="Reorder column"
        disabled={!row.selected}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon className="h-5 w-5" />
      </button>
      <Checkbox
        checked={row.selected}
        onCheckedChange={(checked) => onToggle(row.columnKey, checked)}
      />
      <Input value={row.columnKey} readOnly />
      <div className="text-center text-secondary-text">→</div>
      <Input
        value={row.displayName}
        disabled={!row.selected}
        onChange={(event) =>
          onUpdate(row.columnKey, {
            displayName: event.target.value,
          })
        }
      />
      <div className="text-center text-secondary-text">→</div>
      <SearchableSelect
        mode="single"
        options={formFieldOptions}
        value={row.formField}
        disabled={!row.selected}
        placeholder="Select"
        onChange={(formField) =>
          onUpdate(row.columnKey, {
            formField: formField ? String(formField) : undefined,
          })
        }
      />
    </div>
  );
}

function MasterDataEditor({
  value,
  formFieldOptions,
  formSchema,
  onChange,
  onClose,
  onSave,
}: MasterDataEditorProps) {
  const { datasets } = useBpmDatasets();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const tableOptions = useMemo<SelectOption<string>[]>(
    () =>
      datasets.map((table) => ({
        label: table.code,
        value: table.code,
        key: table.code,
      })),
    [datasets],
  );

  const selectedTable = useMemo(
    () => datasets.find((dataset) => dataset.code === value.config.tableCode),
    [datasets, value.config.tableCode],
  );

  const datasetFields = useMemo(
    () => getDatasetFields(selectedTable),
    [selectedTable],
  );

  const allRows = useMemo(() => {
    const currentColumns = new Map(
      value.config.columns.map((column) => [column.columnKey, column]),
    );
    const datasetFieldMap = new Map(
      datasetFields.map((field) => [field.name, field]),
    );

    const selectedRows = value.config.columns.flatMap((column) => {
      const field = datasetFieldMap.get(column.columnKey);

      if (!field) return [];

      return [
        {
          columnKey: field.name,
          displayName: column.displayName ?? field.name,
          formField: column.formField,
          selected: true,
          type: field.type,
        } satisfies MasterDataRow,
      ];
    });

    const unselectedRows = datasetFields
      .filter((field) => !currentColumns.has(field.name))
      .map((field) => ({
        columnKey: field.name,
        displayName: field.name,
        formField: undefined,
        selected: false,
        type: field.type,
      }));

    return [...selectedRows, ...unselectedRows];
  }, [datasetFields, value.config.columns]);

  const columnOptions = useMemo<SelectOption<string>[]>(
    () =>
      datasetFields.map((field) => ({
        label: field.name,
        value: field.name,
        key: field.name,
      })),
    [datasetFields],
  );
  const canAddFilter =
    getNextAvailableFilterColumnKey(columnOptions, value.config.filters) !== "";

  const updateConfig = (
    updates: Partial<
      Extract<ModalDatasourceValue, { type: "master-data" }>["config"]
    >,
  ) => {
    onChange({
      ...value,
      config: {
        ...value.config,
        ...updates,
      },
    });
  };
  const filterMode = value.config.filterMode ?? "manual";
  const filterExpression =
    value.config.filterExpression ??
    ExpressionDefault[ExpressionType.DropdownFilter];
  const handleFilterModeChange = (mode: ModalDatasourceFilterMode) => {
    updateConfig({
      filterMode: mode,
      filters: mode === "code" ? [] : value.config.filters,
      filterExpression: mode === "code" ? filterExpression : undefined,
    });
  };

  const handleTableChange = (tableCode?: string) => {
    const nextTable = datasets.find((dataset) => dataset.code === tableCode);
    const nextFields = getDatasetFields(nextTable);
    const currentMap = new Map(
      value.config.columns.map((column) => [column.columnKey, column]),
    );

    updateConfig({
      tableCode: tableCode ?? "",
      columns: nextFields.map((field) => ({
        columnKey: field.name,
        displayName: currentMap.get(field.name)?.displayName ?? field.name,
        formField: currentMap.get(field.name)?.formField,
      })),
      filters: [],
    });
  };

  const toggleColumn = (columnKey: string, checked: boolean) => {
    const existing = value.config.columns.find(
      (column) => column.columnKey === columnKey,
    );

    if (checked) {
      updateConfig({
        columns: existing
          ? value.config.columns
          : [
              ...value.config.columns,
              {
                columnKey,
                displayName: columnKey,
                formField: undefined,
              },
            ],
      });
      return;
    }

    updateConfig({
      columns: value.config.columns.filter(
        (column) => column.columnKey !== columnKey,
      ),
    });
  };

  const updateColumn = (
    columnKey: string,
    updates: Partial<ModalDatasourceMasterDataColumn>,
  ) => {
    updateConfig({
      columns: value.config.columns.map((column) =>
        column.columnKey === columnKey ? { ...column, ...updates } : column,
      ),
    });
  };

  const handleColumnDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const activeColumnKey = String(active.id);
    const overColumnKey = String(over.id);
    const oldIndex = value.config.columns.findIndex(
      (column) => column.columnKey === activeColumnKey,
    );
    const newIndex = value.config.columns.findIndex(
      (column) => column.columnKey === overColumnKey,
    );

    if (oldIndex === -1 || newIndex === -1) return;

    updateConfig({
      columns: arrayMove(value.config.columns, oldIndex, newIndex),
    });
  };

  const updateFilter = (
    index: number,
    updates: Partial<ModalDatasourceFilter>,
  ) => {
    const currentColumnKey =
      updates.columnKey ?? value.config.filters[index]?.columnKey;
    const currentType = getDatasetFieldType(datasetFields, currentColumnKey);
    const currentValue =
      updates.value ?? value.config.filters[index]?.value ?? "";

    updateConfig({
      filters: value.config.filters.map((filter, current) => {
        if (current !== index) return filter;

        return {
          ...filter,
          ...updates,
          value:
            currentType === "number" && currentValue !== ""
              ? Number(currentValue)
              : currentValue,
        };
      }),
    });
  };

  const addFilter = () => {
    const nextColumnKey = getNextAvailableFilterColumnKey(
      columnOptions,
      value.config.filters,
    );
    if (!nextColumnKey) return;

    updateConfig({
      filters: [
        ...value.config.filters,
        {
          ...createFilter(),
          columnKey: nextColumnKey,
        },
      ],
    });
  };

  const removeFilter = (index: number) => {
    updateConfig({
      filters: value.config.filters.filter((_, current) => current !== index),
    });
  };

  return (
    <Modal
      isOpen
      close={onClose}
      size="full"
      className="max-w-[1040px] max-h-[90dvh]"
      closeOnOverlayClick={false}
    >
      <div className="flex min-h-0 w-full flex-1 flex-col gap-[30px] bg-white p-[30px]">
        <h2 className="text-center text-[24px] font-semibold leading-[30px] text-dark">
          Master Data Config
        </h2>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2.5">
              <Label className="text-base font-medium text-dark">
                Target Table
              </Label>
              <SearchableSelect
                mode="single"
                options={tableOptions}
                value={value.config.tableCode}
                placeholder="Select table"
                onChange={(tableCode) =>
                  handleTableChange(tableCode ? String(tableCode) : undefined)
                }
              />
            </div>

            <div className="flex flex-col gap-2.5">
              <Label className="text-base font-medium text-dark">
                Display Columns
              </Label>

              <div className="grid grid-cols-[20px_20px_minmax(0,1fr)_24px_minmax(0,1fr)_24px_minmax(0,1fr)] gap-2.5 px-5 text-base font-medium text-dark">
                <div />
                <div />
                <div>Data field</div>
                <div />
                <div>Display field</div>
                <div />
                <div>Set Mapping Value</div>
              </div>
              {allRows.length === 0 && (
                <div className="flex max-h-[288px] min-h-20 bg-gray items-center justify-center rounded-[8px] bg-gray-1 text-base font-normal text-secondary-text">
                  No data
                </div>
              )}

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleColumnDragEnd}
              >
                <SortableContext
                  items={allRows.map((row) => row.columnKey)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex flex-col gap-2.5">
                    {allRows.map((row) => (
                      <MasterDataColumnRow
                        key={row.columnKey}
                        row={row}
                        formFieldOptions={formFieldOptions}
                        onToggle={toggleColumn}
                        onUpdate={updateColumn}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>

            <div className="flex flex-col gap-2.5">
              <div className="flex items-center justify-between gap-4">
                <Label className="text-base font-medium text-dark">
                  Filter Configuration
                </Label>
                <CodeToggle
                  value={filterMode}
                  onChange={handleFilterModeChange}
                />
              </div>

              {filterMode === "code" ? (
                <div className="flex flex-col gap-1.5">
                  <Label className="text-base font-medium text-dark">
                    Expression
                  </Label>
                  <CodeEditButton
                    variant="reference"
                    formSchema={formSchema}
                    value={filterExpression}
                    trigger={filterExpression}
                    onSave={(nextExpression) => {
                      updateConfig({
                        filterMode: "code",
                        filterExpression: nextExpression,
                      });
                    }}
                  />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-[minmax(0,1fr)_100px_minmax(0,1fr)_44px] gap-2.5 text-base font-medium text-dark">
                    <div>Filter Column</div>
                    <div>Operator</div>
                    <div>Value</div>
                    <div />
                  </div>

                  <div className="flex flex-col gap-2.5">
                    {value.config.filters.map((filter, index) => (
                      <div
                        key={`${filter.columnKey}-${index}`}
                        className="grid grid-cols-[minmax(0,1fr)_100px_minmax(0,1fr)_44px] gap-2.5"
                      >
                        <Select
                          options={getAvailableFilterColumnOptions(
                            columnOptions,
                            value.config.filters,
                            index,
                          )}
                          value={filter.columnKey}
                          placeholder="Select column"
                          onChange={(columnKey: string) =>
                            updateFilter(index, { columnKey })
                          }
                        />
                        <Select
                          options={operatorOptions}
                          value={filter.operator}
                          placeholder="="
                          onChange={(operator: Operator) =>
                            updateFilter(index, {
                              operator,
                            })
                          }
                        />
                        <Input
                          value={String(filter.value ?? "")}
                          onChange={(event) =>
                            updateFilter(index, { value: event.target.value })
                          }
                          placeholder="Value"
                        />
                        <Button
                          type="button"
                          variant="icon"
                          size="icon"
                          className="h-11 w-11"
                          onClick={() => removeFilter(index)}
                          aria-label="Remove filter"
                        >
                          <DeleteIcon className="h-6 w-6 text-primary-text" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className={cn(
                      "w-fit px-0 text-giant-blue text-base",
                      "disabled:bg-transparent disabled:text-secondary-text disabled:no-underline",
                    )}
                    onClick={addFilter}
                    icon={<Plus className="h-4 w-4" />}
                    disabled={!canAddFilter}
                  >
                    Add condition
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-[18px]">
          <Button
            type="button"
            variant="secondary"
            className="w-[190px]"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button type="button" className="w-[190px]" onClick={onSave}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export const ModalDatasourceAttribute = createAttributeComponent(
  modalDatasourceAttribute,
  function ModalDatasourceAttribute(props) {
    const [builderStore] = useAtom(builderStoreAtom);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const currentValue = useMemo(
      () => normalizeCurrentValue(props.attribute.value),
      [props.attribute.value],
    );
    const [draftValue, setDraftValue] =
      useState<ModalDatasourceValue>(currentValue);

    useEffect(() => {
      if (!isModalOpen) {
        setDraftValue(currentValue);
      }
    }, [currentValue, isModalOpen]);

    const formFieldOptions = useMemo<SelectOption<string>[]>(() => {
      return Object.values(builderStore?.getSchema().entities ?? {}).map(
        (entity) => {
          const currentEntity = entity as BuilderEntityLike;
          return {
            label:
              (currentEntity.attributes.label.isReference
                ? currentEntity.attributes.label.reference
                : currentEntity.attributes.label.value) ?? "Unknown",
            value: currentEntity.attributes.name,
            key: currentEntity.attributes.name,
          };
        },
      );
    }, [builderStore]);
    const formSchema = builderStore?.getSchema();

    const handleTypeChange = (type: string) => {
      const nextValue = createDefaultModalDatasourceValue(
        type as ModalDatasourceValue["type"],
      );
      props.setValue(nextValue);
      setDraftValue(nextValue);
    };

    const summary = useMemo(() => {
      if (currentValue.type === "master-data") {
        return currentValue.config.tableCode
          ? `Master Data: ${currentValue.config.tableCode}`
          : "Master Data config not set";
      }

      return currentValue.config.api_config.url
        ? `External API: ${currentValue.config.api_config.url}`
        : "External API config not set";
    }, [currentValue]);

    const errorMessage = formatError(
      props.attribute.value,
      props.attribute.error,
    )?._errors?.[0];

    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label className="font-body-medium-medium block">Data Source</Label>
          <RadioGroup
            name={props.attribute.name}
            value={currentValue.type}
            onChange={handleTypeChange}
            options={typeOptions}
            className="flex gap-4"
          />
        </div>
        <Button
          type="button"
          variant="secondary"
          className="w-full bg-white"
          onClick={() => setIsModalOpen(true)}
        >
          {draftValue.type === "external-api"
            ? "Edit API Config"
            : "Master Data Config"}
        </Button>

        {errorMessage && <ValidationError>{errorMessage}</ValidationError>}

        {isModalOpen &&
          (draftValue.type === "external-api" ? (
            <ExternalApiEditor
              value={draftValue}
              formFieldOptions={formFieldOptions}
              formSchema={formSchema}
              onChange={setDraftValue}
              onClose={() => {
                setDraftValue(currentValue);
                setIsModalOpen(false);
              }}
              onSave={() => {
                props.setValue(draftValue);
                setIsModalOpen(false);
              }}
            />
          ) : (
            <MasterDataEditor
              value={draftValue}
              formFieldOptions={formFieldOptions}
              formSchema={formSchema}
              onChange={setDraftValue}
              onClose={() => {
                setDraftValue(currentValue);
                setIsModalOpen(false);
              }}
              onSave={() => {
                props.setValue(draftValue);
                setIsModalOpen(false);
              }}
            />
          ))}
      </div>
    );
  },
);
